import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
} from '@angular/core';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { markdownToSanitizedHtml } from './chat-markdown.render';
import { PalantirToastService } from '@flux/design-system';
import { fluxLogger } from '@core/services/logger/logger.instance';

const logger = fluxLogger.extend('chat').extend('chat-markdown');

/**
 * Renderiza o markdown de uma mensagem da Iara de forma fluida e uniforme:
 * tipografia, listas, citações, tabelas, links seguros e blocos de código com
 * realce de sintaxe (highlight.js) + botão de copiar. HTML sanitizado (DOMPurify).
 */
@Component({
  selector: 'app-chat-markdown',
  standalone: true,
  template: `<div class="iara-markdown" [innerHTML]="html()"></div>`,
  styleUrl: './chat-markdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Estilos precisam atingir o HTML injetado via [innerHTML]; classes são prefixadas
  // (.iara-markdown / .iara-code / .iara-md__link) para não vazar globalmente.
  encapsulation: ViewEncapsulation.None,
  host: { '(click)': 'onClick($event)' },
})
export class ChatMarkdownComponent {
  private readonly toastService = inject(PalantirToastService);

  private readonly sanitizer = inject(DomSanitizer);

  public readonly content = input<string>('');

  protected readonly html = computed<SafeHtml>(() =>
    this.sanitizer.bypassSecurityTrustHtml(markdownToSanitizedHtml(this.content())),
  );

  protected onClick(event: MouseEvent): void {
    const trigger = (event.target as HTMLElement).closest('[data-action="copy-code"]');
    if (!trigger) return;
    const code = trigger.closest('.iara-code')?.querySelector('code')?.textContent;
    if (code)
      void navigator.clipboard?.writeText(code).then(
        () => {
          this.toastService.showSuccess('Copiado para a área de transferência!');
        },
        (err) => {
          logger.error('Erro ao copiar:', err);
          this.toastService.showError('Erro ao copiar para a área de transferência.');
        },
      );
  }
}
